/* eslint-disable indent */
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TUserSchema } from '@/schemas/user/user.schema';
import { editUserSchema } from '@/schemas/user/edit-user.schema';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { UserProfileManagement } from '../components/UserProfileManagement';
import {
  useGetAssignedAdmin,
  useGetAssignedModerator,
  useGetUserById,
  useUpdatePassword,
  useUpdateUser,
} from '@/hooks/user.hooks';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/manage-user.styles.css';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { UserPasswordManagement } from '../components/UserPasswordManagement';
import {
  resetPasswordSchema,
  TResetPassword,
} from '@/schemas/auth/reset-password.schema';
import { UserRolesManagement } from '../components/UserRolesManagement';
import Card from '@/components/Card';
import { TGroupSchema } from '@/schemas/group/group.schema';
import { RoleName } from '@/api/entities/user-role-type';
import {
  useGetAssignedGroupsToUser,
  useGetUnassignedGroupsToUser,
} from '@/hooks/group.hooks';
import { TCompanySchema } from '@/schemas/company/company.schema';
import {
  useGetAssignedCompaniesToUser,
  useGetUnassignedCompaniesToUser,
} from '@/hooks/company.hooks';
import { TBranchSchema } from '@/schemas/branch/branch.schema';
import {
  useGetAssignedBranchesToUser,
  useGetUnassignedBranchesToUser,
} from '@/hooks/branch.hooks';
import { useTranslation } from 'react-i18next';
import { useGetTab } from '@/hooks/tab.hooks';
import { EditUserTab } from '@/type/ui/tab/userTab';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { useGetAllVehiculeChecks } from '@/hooks/vehicule-check.hooks';
import { AllSelectedTableItemsProvider } from '@/providers/AllSelectedTableItemsProvider';
import { VehiculeCheckTableView } from '@/modules/VehiculeCheck/components/VehiculeCheckTableView';
import { TVehiculeCheckSchema } from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckTableHeaders } from '@/modules/VehiculeCheck/components/VehiculeCheckTableHeaders';
import { VehiculeCheckTableData } from '@/modules/VehiculeCheck/components/VehiculeCheckTableData';
import { TitleSection } from '@/components/Header/Section/TitleSection';

export const EditUserPage = () => {
  const { t } = useTranslation('user');
  const { t: tInspection } = useTranslation('inspection');
  const { primaryRole } = useAuth();
  const { hasNoRole, isAdmin, isModerator, isBranchAgent } = useUserRole();
  const { id } = useParams();
  const { data, isLoading } = useGetUserById(`${id}`);
  const navigate = useNavigate();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditUserTab.PROFILE,
  });

  const byUserFullnameText = isLoading
    ? ''
    : `${t('common:by').toLowerCase()} ${data?.data?.firstname} ${
        data?.data?.lastname
      }`;

  /**
   * === Update User section ===
   */
  const {
    watch: watchUser,
    control: userControl,
    register: registerUser,
    setValue: setUserValue,
    setError: setUserError,
    handleSubmit: handleSubmitUser,
    formState: { errors: userErrors, isSubmitting: isSubmittingUser },
  } = useForm<TUserSchema>({
    resolver: zodResolver(editUserSchema),
  });

  const { mutate: mutateUpdateUser, isPending: isPendingUpdateUser } =
    useUpdateUser({
      t,
      handleSuccess: () => navigate('/users'),
    });

  const onSubmitUserProfile = (user: TUserSchema) => {
    const userWithId = {
      id,
      ...user,
    };
    mutateUpdateUser(userWithId);
  };
  /**
   * === END OF Update User section ===
   */

  /**
   * === Reset Password section ===
   */
  const {
    register: registerResetPassword,
    handleSubmit: handleSubmitResetPassword,
    formState: {
      errors: resetPasswordErrors,
      isSubmitting: isSubmittingResetPassword,
    },
  } = useForm<TResetPassword>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const { mutate: mutateResetPassword, isPending: isPendingResetPassword } =
    useUpdatePassword({
      t,
      handleSuccess: () => navigate('/users'),
    });

  const onSubmitResetPassword = (data: TResetPassword) => {
    mutateResetPassword({
      id,
      password: data.password,
    });
  };
  /**
   * === END OF Reset Password section ===
   */

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/users'}
        title={t('common:user')}
        subTitle={
          data?.data
            ? `${data?.data?.firstname} ${data?.data?.lastname}`
            : undefined
        }
        subTitleIconClassName="ki-users"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('userList')} linkTo="/users" />
            <BreadcrumbItem
              text={
                data?.data
                  ? `${data?.data?.firstname} ${data?.data?.lastname}`
                  : undefined
              }
              active
            />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text="Cet utilisateur n'existe pas ..." />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:profile')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditUserTab.PROFILE}
              onChange={() => navigateToTab(EditUserTab.PROFILE)}
            />
            <TabItem
              text={t('common:inspections')}
              leftIcon={<i className="ki-outline ki-tablet-ok" />}
              active={currentTab === EditUserTab.VEHICULE_CHECKS}
              onChange={() => navigateToTab(EditUserTab.VEHICULE_CHECKS)}
            />
            <TabItem
              text={t('common:security')}
              leftIcon={<i className="ki-outline ki-security-user !text-xl" />}
              active={currentTab === EditUserTab.SECURITY}
              onChange={() => navigateToTab(EditUserTab.SECURITY)}
            />
          </TabList>
          {currentTab === EditUserTab.PROFILE && (
            <div className="flex flex-col gap-12">
              <UserProfileManagement<TUserSchema>
                t={t}
                roleType={primaryRole}
                data={!isLoading && data?.data}
                watch={watchUser}
                control={userControl}
                register={registerUser}
                setValue={setUserValue}
                setError={setUserError}
                errors={userErrors}
                submitDisabled={isSubmittingUser || isPendingUpdateUser}
                onSubmit={handleSubmitUser(onSubmitUserProfile)}
              />
              {!(hasNoRole || isModerator || isBranchAgent) && (
                <Card
                  title={t('common:roles')}
                  bodyClassName="py-7 flex flex-col gap-5"
                >
                  {/* <h4 className="text-sm text-gray-5B5B5B mb-1"> */}
                  <h4 className="text-sm text-gray-600 font-medium mb-1">
                    {`${t('selectRolesOneOrMoreRolesBelow')} :`}
                  </h4>
                  {isAdmin && (
                    <>
                      <UserRolesManagement<TUserSchema>
                        t={t}
                        userId={id}
                        roleName={RoleName.ADMIN}
                        roleNameLabel={t('common:administrator')}
                        useGetAssignedEntities={useGetAssignedAdmin}
                      />
                      <UserRolesManagement<TUserSchema>
                        t={t}
                        userId={id}
                        roleName={RoleName.MODERATOR}
                        roleNameLabel={t('common:moderator')}
                        useGetAssignedEntities={useGetAssignedModerator}
                      />
                      <UserRolesManagement<TGroupSchema>
                        t={t}
                        userId={id}
                        entityField="groupId"
                        roleName={RoleName.GROUP_MANAGER}
                        roleNameLabel={t('common:groupManager')}
                        assignEntityText={t('addOneOrMoreGroupsToAssign')}
                        mainContentNotFoundText={t(
                          'assignOneOrMoreGroupsToUser',
                        )}
                        modalTitle={t('selectOneOrMoreGroupsToAssign')}
                        modalDataNotFoundText={t('noGroupToAssign')}
                        useGetUnassignedEntities={useGetUnassignedGroupsToUser}
                        useGetAssignedEntities={useGetAssignedGroupsToUser}
                      />
                    </>
                  )}
                  <UserRolesManagement<TCompanySchema>
                    t={t}
                    userId={id}
                    entityField="companyId"
                    roleName={RoleName.COMPANY_MANAGER}
                    roleNameLabel={t('common:companyManager')}
                    assignEntityText={t('addOneOrMoreCompaniesToAssign')}
                    mainContentNotFoundText={t(
                      'assignOneOrMoreCompaniesToUser',
                    )}
                    modalTitle={t('selectOneOrMoreCompaniesToAssign')}
                    modalDataNotFoundText={t('noCompanyToAssign')}
                    useGetUnassignedEntities={useGetUnassignedCompaniesToUser}
                    useGetAssignedEntities={useGetAssignedCompaniesToUser}
                  />
                  <UserRolesManagement<TBranchSchema>
                    t={t}
                    userId={id}
                    entityField="branchId"
                    roleName={RoleName.BRANCH_MANAGER}
                    roleNameLabel={t('common:branchManager')}
                    assignEntityText={t('addOneOrMoreBranchesToAssign')}
                    mainContentNotFoundText={t('assignOneOrMoreBranchesToUser')}
                    modalTitle={t('selectOneOrMoreBranchesToAssign')}
                    modalDataNotFoundText={t('noBranchToAssign')}
                    useGetUnassignedEntities={useGetUnassignedBranchesToUser}
                    useGetAssignedEntities={useGetAssignedBranchesToUser}
                  />
                  <UserRolesManagement<TBranchSchema>
                    t={t}
                    userId={id}
                    entityField="branchId"
                    roleName={RoleName.BRANCH_AGENT}
                    roleNameLabel={t('common:branchAgent')}
                    assignEntityText={t('addOneOrMoreBranchesToAssign')}
                    mainContentNotFoundText={t('assignOneOrMoreBranchesToUser')}
                    modalTitle={t('selectOneOrMoreBranchesToAssign')}
                    modalDataNotFoundText={t('noBranchToAssign')}
                    useGetUnassignedEntities={useGetUnassignedBranchesToUser}
                    useGetAssignedEntities={useGetAssignedBranchesToUser}
                  />
                </Card>
              )}
            </div>
          )}
          {currentTab === EditUserTab.VEHICULE_CHECKS && (
            <AllSelectedTableItemsProvider>
              <VehiculeCheckTableView<TVehiculeCheckSchema>
                t={tInspection}
                cardTitle={
                  <TitleSection
                    text={`${t(
                      'common:inspectionsPerformed',
                    )} ${byUserFullnameText}`}
                    defaultIconClassName="ki-user-tick"
                  />
                }
                receivedActionUserId={id}
                useGetAllData={useGetAllVehiculeChecks}
                tableHeaders={VehiculeCheckTableHeaders}
                tableData={VehiculeCheckTableData}
              />
            </AllSelectedTableItemsProvider>
          )}
          {currentTab === EditUserTab.SECURITY && (
            <div className="flex flex-col gap-12">
              <UserPasswordManagement<TResetPassword>
                t={t}
                register={registerResetPassword}
                errors={resetPasswordErrors}
                submitDisabled={
                  isSubmittingResetPassword || isPendingResetPassword
                }
                onSubmit={handleSubmitResetPassword(onSubmitResetPassword)}
              />
            </div>
          )}
        </div>
      )}
      {(isLoading ||
        isSubmittingUser ||
        isPendingUpdateUser ||
        isSubmittingResetPassword ||
        isPendingResetPassword) && <Loader />}
    </>
  );
};
